import React, { memo } from 'react';
import { useWatch } from 'react-hook-form';
import classNames from 'classnames';
import { Values } from '@lib/interfaces/form';
import styles from './Form.module.scss';

export interface GroupFieldWrapperProps {
  children: React.ReactNode;
  className?: string;
  element?: React.JSX.ElementType;
  componentProps?: Record<string, unknown>;
  formItemsClassName?: string;
  isVisibleFn?: (values: Values, name?: string) => boolean;
  isVisible?: boolean;
  name: string;
}

/**
 * Wrapper component for grouped fields that handles visibility using isVisibleFn.
 * Uses useWatch to reactively show/hide the group based on form values.
 */
function GroupFieldWrapper(props: GroupFieldWrapperProps) {
  const {
    children,
    className,
    element: Element,
    componentProps,
    formItemsClassName,
    isVisibleFn,
    isVisible = true,
    name,
  } = props;

  const watch = useWatch({
    disabled: isVisibleFn === undefined,
  });

  // Check visibility using isVisibleFn if provided
  if (isVisibleFn && !isVisibleFn(watch as Values, name)) {
    return null;
  }

  // Hide based on step visibility
  if (!isVisible) {
    return (
      <div
        className={classNames(
          styles.formItemColumn,
          styles.formItemGroup,
          className,
        )}
        data-test-id={`form__group-${name}`}
        style={{ display: 'none' }}
      >
        {Element ? (
          <Element {...componentProps}>
            <div className={classNames(styles.formItems, formItemsClassName)}>
              {children}
            </div>
          </Element>
        ) : (
          <div className={classNames(styles.formItems, formItemsClassName)}>
            {children}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={classNames(
        styles.formItemColumn,
        styles.formItemGroup,
        className,
      )}
      data-test-id={`form__group-${name}`}
    >
      {Element ? (
        <Element {...componentProps}>
          <div className={classNames(styles.formItems, formItemsClassName)}>
            {children}
          </div>
        </Element>
      ) : (
        <div className={classNames(styles.formItems, formItemsClassName)}>
          {children}
        </div>
      )}
    </div>
  );
}

export default memo(GroupFieldWrapper);
